import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Checkbox,
  Container,
  Divider,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import appStyles from '../../../../config/appStyles';
import ChangePaymentMethodModal from '../../components/ChangePaymentMethodModal';
import { useStores } from '../../stores';
import Progress from '../../components/common/Progress';
import { getKeys } from '../../utils/dictionary';
import axios from 'axios';
import { detectAlpha2Country } from '../../../../utils/country';
import Button from '../../components/common/Button';
import ContactsInfo from '../../components/ContactsInfo';
import PaymentResult from '../../components/common/PaymentResult';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import Alert from '../../components/common/Alert';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import storage from '../../utils/storage';

const useStyles = makeStyles((theme) => appStyles.reactivationCampaign(theme));

const ReactivationCampaign = observer(() => {
  const classes = useStyles();
  const [contactInfo, setContactInfo] = useState([]);
  const [image, setImage] = useState({});
  const [translations, setTranslations] = useState(null);
  const [isChecked, setChecked] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    title: '',
    text: '',
    icon: null,
  });
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const { uiStore, userStore, orderStore } = useStores();
  const { isReady, isLoading, setIsLoading, getAccountInfo } = userStore;
  const { currentOrder, setCurrentOrder, getOrders } = orderStore;
  const [showProcessingLabel, setProcessingLabel] = useState(false);

  const params = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
      productCode: urlParams.get('productCode'),
      orderId: urlParams.get('orderId'),
      promoCode: urlParams.get('promoCode'),
      autologin: urlParams.get('autologin'),
      type: urlParams.get('type'),
      resultCode: urlParams.get('resultCode'),
    };
  }, []);
  const match = (params.promoCode || '').match(/LEVEL_(\d)/);
  const level = match ? parseInt(match[1], 10) : null;
  const country = useMemo(() => detectAlpha2Country(), []);
  const reactivationType = {
    active: 'active',
    passive: 'passive',
  };

  useEffect(() => {
    let isMounted = true;

    const initializePage = async () => {
      if (!params.productCode || !params.orderId) return;

      setIsLoading(true);

      try {
        if (params.autologin && isReady) {
          getAccountInfo();
          getOrders();
        }

        const [contactRes, productRes, translationData] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_CMS_ENDPOINT}${process.env.REACT_APP_GET_CONTACT}${uiStore.site}`,
          ),
          axios.post(
            `${process.env.REACT_APP_API_SERVICE_URL}product/summary`,
            {
              productIds: [params.productCode],
              country,
            },
          ),
          getKeys({
            title: 'customerPortal.reactivation.title',
            content: 'customerPortal.reactivation.content',
            title_level_1: 'customerPortal.reactivation.title_level_1',
            content_level_1: 'customerPortal.reactivation.content_level_1',
            title_level_2: 'customerPortal.reactivation.title_level_2',
            content_level_2: 'customerPortal.reactivation.content_level_2',
            title_level_3: 'customerPortal.reactivation.title_level_3',
            content_level_3: 'customerPortal.reactivation.content_level_3',
            cta: 'customerPortal.reactivation.cta',
            delete: 'customerPortal.reactivation.info.delete',
            gifts: 'customerPortal.reactivation.info.gifts',
            license: 'customerPortal.reactivation.info.license',
            securePay: 'customerPortal.reactivation.info.securePay',
            success_title: 'customerPortal.reactivation.popup.success_title',
            success_content:
              'customerPortal.reactivation.popup.success_content',
            error_title: 'customerPortal.reactivation.popup.error_title',
            error_content: 'customerPortal.reactivation.popup.error_content',
            legalTerms: 'customerPortal.reactivation.legalTerms',
            legalTermsLinkText:
              'customerPortal.reactivation.legalTermsLinkText',
            legalTermsLinkUrl: 'customerPortal.reactivation.legalTermsLinkUrl',
            paymentUpdateLabel:
              'customerPortal.reactivation.paymentUpdateLabel',
            paymentUpdateButtonLabel:
              'customerPortal.reactivation.paymentUpdateButtonLabel',
            paymentUpdateSuccessMessage:
              'customerPortal.reactivation.paymentUpdateSuccessMessage',
          }),
        ]);
        setContactInfo(contactRes?.data?.data?.content || []);
        setImage(
          (process.env.REACT_APP_BRAND === 'PW'
            ? productRes?.data?.data[params.productCode]?.productImage
            : productRes?.data?.data[params.productCode]?.marketingAutomation
                ?.abandonedCart?.productImageWide) || {},
        );
        translationData.content = translationData.content.replace(
          /\$NAME.COLLECTION\$/g,
          productRes?.data?.data[params.productCode]?.short,
        );
        translationData.content_level_1 =
          translationData.content_level_1.replace(
            /\$NAME.COLLECTION\$/g,
            productRes?.data?.data[params.productCode]?.short,
          );
        translationData.content_level_2 =
          translationData.content_level_2.replace(
            /\$NAME.COLLECTION\$/g,
            productRes?.data?.data[params.productCode]?.short,
          );
        translationData.content_level_3 =
          translationData.content_level_3.replace(
            /\$NAME.COLLECTION\$/g,
            productRes?.data?.data[params.productCode]?.short,
          );

        setTranslations(translationData);

        if (!isMounted) return;

        const orders = await window.gproxy.customer.order.list();

        if (isMounted && orders?.length > 0) {
          const found = orders.find((o) => o.id === params.orderId);
          if (found) {
            setCurrentOrder(found);
          }
        }

        const productShortName =
          productRes?.data?.data?.[params.productCode]?.short || '';
        const processedTranslations = Object.keys(translationData).reduce(
          (acc, key) => {
            acc[key] =
              typeof translationData[key] === 'string'
                ? translationData[key].replace(
                    /\$NAME.COLLECTION\$/g,
                    productShortName,
                  )
                : translationData[key];
            return acc;
          },
          {},
        );
        processedTranslations.content = translationData.content.replace(
          /\$NAME.COLLECTION\$/g,
          productRes?.data?.data[params.productCode]?.short,
        );
        processedTranslations.content_level_1 =
          translationData.content_level_1.replace(
            /\$NAME.COLLECTION\$/g,
            productRes?.data?.data[params.productCode]?.short,
          );
        processedTranslations.content_level_2 =
          translationData.content_level_2.replace(
            /\$NAME.COLLECTION\$/g,
            productRes?.data?.data[params.productCode]?.short,
          );
        processedTranslations.content_level_3 =
          translationData.content_level_3.replace(
            /\$NAME.COLLECTION\$/g,
            productRes?.data?.data[params.productCode]?.short,
          );

        setTranslations(processedTranslations);
        setImage(
          (process.env.REACT_APP_BRAND === 'PW'
            ? productRes?.data?.data[params.productCode]?.productImage
            : productRes?.data?.data[params.productCode]?.marketingAutomation
                ?.abandonedCart?.productImageWide) || {},
        );
      } catch (error) {
        console.error('Errore inizializzazione:', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializePage();

    return () => {
      isMounted = false;
    };
  }, [
    uiStore.site,
    params.productCode,
    params.orderId,
    params.autologin,
    isReady,
    country,
    setIsLoading,
    getAccountInfo,
    setCurrentOrder,
  ]);

  useEffect(() => {
    const hasCookie = document.cookie
      .split(';')
      .some((c) => c.trim().startsWith('processingLabelForPayment='));

    if (hasCookie) {
      setProcessingLabel(true);
    }
  }, []);

  const toggleEditPayment = () => setEditPaymentOpen((prev) => !prev);

  const currentTitle =
    translations?.[`title_level_${level}`] || translations?.title;
  const currentContent =
    translations?.[`content_level_${level}`] || translations?.content;
  const legalTerms = translations?.legalTerms.replace(
    /\$LEGAL_TERMS\$/g,
    `<a href='${translations?.legalTermsLinkUrl}' target='_blank'>${translations?.legalTermsLinkText}</a>`,
  );

  const handleClick = async () => {
    try {
      setIsLoading(true);
      const res = await window.gproxy.customer.order.reactivate({
        orderId: params.orderId,
        promoCode: params.promoCode,
      });
      if (res) {
        setIsLoading(false);
        setAlert({
          open: true,
          title: translations.success_title,
          text: translations.success_content,
          icon: <CheckCircleIcon style={{ color: 'green' }} />,
        });
        if (window.snakeAnalytics) {
          window.snakeAnalytics.push([
            'event',
            'im_ form_submit',
            {
              eventData: {
                im_collection_name: params.productCode,
              },
            },
          ]);
        }
      } else {
        setIsLoading(false);
        setAlert({
          open: true,
          title: translations.error_title,
          text: translations.error_content,
          icon: <ErrorIcon style={{ color: 'red' }} />,
        });
        if (window.snakeAnalytics) {
          window.snakeAnalytics.push([
            'event',
            'im_form_error',
            {
              eventData: {
                im_error_message: 'error',
              },
            },
          ]);
        }
      }
    } catch (error) {
      setIsLoading(false);
      setAlert({
        open: true,
        title: translations.error_title,
        text: translations.error_content,
        icon: <ErrorIcon style={{ color: 'red' }} />,
      });
      if (window.snakeAnalytics) {
        window.snakeAnalytics.push([
          'event',
          'im_form_error',
          {
            eventData: {
              im_error_message: error?.errorDetails,
            },
          },
        ]);
      }
    }
  };

  if (!params.productCode || !params.orderId || !params.promoCode) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <Box className={classes.container}>
          Parametri mancanti o non validi.
        </Box>
      </Container>
    );
  }

  if (isLoading || Object.keys(translations || {}).length === 0)
    return <Progress variant="fullscreen" />;

  return (
    <>
      <div className={classes.root}>
        <div className={classes.container}>
          <div className={classes.imageContainer}>
            <img
              src={image.src}
              alt={image.alt || 'product'}
              title={image.title}
              style={{
                maxHeight: '550px',
                minWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
          <div>
            <Box display="flex" flexDirection="column" sx={{ gap: '32px' }}>
              <Typography className={classes.title}>{currentTitle}</Typography>
              <Typography className={classes.content}>
                {currentContent}
              </Typography>
              {currentOrder && params.type === reactivationType.passive && (
                <>
                  <Divider />
                  <Box className={classes.boxPassive}>
                    <Typography className={classes.paymentTitle}>
                      {translations.paymentUpdateLabel}
                    </Typography>
                    <Link
                      className={classes.changePaymentLink}
                      onClick={toggleEditPayment}>
                      <CreditCardIcon fontSize="small" />
                      <span>{translations.paymentUpdateButtonLabel}</span>
                    </Link>

                    <ChangePaymentMethodModal
                      open={editPaymentOpen}
                      editPaymentAuto={false}
                      onClose={() => {
                        toggleEditPayment();
                      }}
                      isReactivation={true}
                    />
                    <PaymentResult isReactivation />
                    {(params.resultCode === 'authorised' ||
                      currentOrder.payment.updating ||
                      showProcessingLabel) && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}>
                        <CheckIcon
                          fontSize="small"
                          className={classes.paymentSuccessIcon}
                        />
                        <Typography className={classes.paymentSuccessMessage}>
                          {translations.paymentUpdateSuccessMessage}
                        </Typography>
                      </div>
                    )}
                  </Box>
                </>
              )}

              <Box display="flex" flexDirection="column" sx={{ gap: '12px' }}>
                <div className={classes.terms}>
                  <Checkbox
                    id="legalTerms"
                    name="legalTerms"
                    checked={isChecked}
                    onChange={() => setChecked((prev) => !prev)}
                  />
                  <label
                    htmlFor="legalTerms"
                    dangerouslySetInnerHTML={{
                      __html: legalTerms,
                    }}
                  />
                </div>

                <Button
                  className={classes.button}
                  disabled={!isChecked}
                  onClick={handleClick}
                  next>
                  {translations?.cta}
                </Button>
              </Box>

              <Box className={classes.gridIcons}>
                <Box className={classes.iconContainer}>
                  <img
                    className={classes.iconImage}
                    src={
                      process.env.REACT_APP_BRAND === 'PW'
                        ? 'https://cdn.deagostini.com/dea/live/media/dea-icons-cancel-anytime-4/dea-icons-cancel-anytime-4_optmzd_80.webp'
                        : 'https://cdn.deagostini.com/fh/live/media/icon-cancel-3/icon-cancel-3_optmzd_80.png'
                    }
                    alt="cancel-anytime"
                  />
                  <span>{translations?.delete}</span>
                </Box>
                <Box className={classes.iconContainer}>
                  <img
                    className={classes.iconImage}
                    src={
                      process.env.REACT_APP_BRAND === 'PW'
                        ? 'https://cdn.deagostini.com/dea/live/media/dea-icons-free-gifts-5/dea-icons-free-gifts-5_optmzd_80.webp'
                        : 'https://cdn.deagostini.com/fh/live/media/icon-gift-3/icon-gift-3_optmzd_80.png'
                    }
                    alt="free-gifts"
                  />
                  <span>{translations?.gifts}</span>
                </Box>
                <Box className={classes.iconContainer}>
                  <img
                    className={classes.iconImage}
                    src={
                      process.env.REACT_APP_BRAND === 'PW'
                        ? 'https://cdn.deagostini.com/dea/live/media/dea-icons-officially-licensed-5/dea-icons-officially-licensed-5_optmzd_80.webp'
                        : 'https://cdn.deagostini.com/fh/live/media/icon-licensed-3/icon-licensed-3.png'
                    }
                    alt="officially-licensed"
                  />
                  <span>{translations?.license}</span>
                </Box>
                <Box className={classes.iconContainer}>
                  <img
                    className={classes.iconImage}
                    src={
                      process.env.REACT_APP_BRAND === 'PW'
                        ? 'https://cdn.deagostini.com/dea/live/media/dea-icons-secure-payment-3/dea-icons-secure-payment-3_optmzd_80.webp'
                        : 'https://cdn.deagostini.com/fh/live/media/icon-secure-3/icon-secure-3_optmzd_80.png'
                    }
                    alt="secure-payment"
                  />
                  <span>{translations?.securePay}</span>
                </Box>
              </Box>
            </Box>
          </div>
        </div>
      </div>

      <ContactsInfo contactInfo={contactInfo} />

      <Alert
        id="status-alert"
        open={alert.open}
        onClose={() => {
          setAlert((prev) => ({ ...prev, open: false }));
          window.location.href = `${
            process.env.REACT_APP_BRAND === 'PW'
              ? 'https://deagostini.com/'
              : 'https://fanhome.com/'
          }${country}`;
        }}
        title={alert.title}
        text={alert.text}
        icon={alert.icon}
      />
    </>
  );
});

export default ReactivationCampaign;
